/**
 * Calendar component responsibilities:
 * - Display a month grid of days based on the provided currentDate.
 * - Highlight available appointment slots and the first available date.
 * - Support filtering by appointment type.
 * - Navigate between months and jump to today.
 * - Sync selected dates with parent via onDaySelected and onChangeDate.
 * - Update timezone abbreviation
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useReactiveVar } from '@apollo/client'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import {
  addDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  formatISO,
  intlFormat,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import PropTypes from 'prop-types'
import { Analytics } from 'shared-components'
import { Divider } from 'shared-components'

import { store, timezoneAbbr } from '../../cache'
import i18next from '../../i18n'
import { getAvailability } from '../../utils/getAvailability'
import { newestToOldest } from '../../utils/newestToOldest'
import { Day } from '../Day'
import { FirstAvailable } from '../FirstAvailable'
import { NextMonth } from './NextMonth'
import { PreviousMonth } from './PreviousMonth'

import './style.css'

export const Calendar = ({
  onDaySelected,
  selectedDay,
  selectedAppointmentType,
  currentDate,
  onChangeDate,
  appointments,
}) => {
  // Compute weeks to render for the current month view
  const today = new Date()
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd })

  // Initialize i18n translation and locale settings
  const { t } = useTranslation()
  const langCode = i18next.language

  // Format the month and year header for display
  const currentMonthYear = intlFormat(
    currentDate,
    {
      year: 'numeric',
      month: 'long',
    },
    { locale: langCode }
  )

  // Generate abbreviated weekday labels based on locale
  const localizedWeekdays = () => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 })
    const weekdays = Array.from({ length: 7 }).map((_, i) => {
      return intlFormat(
        addDays(weekStart, i),
        { weekday: 'short' },
        { locale: langCode }
      )
    })
    return weekdays
  }
  const weekdays = localizedWeekdays()

  // Retrieve location and partner identifiers for queries and analytics
  const { locationId, partnerId } = useReactiveVar(store)

  // Available appointments passed in from useAppointmentAvailability hook
  const availableAppointments = appointments

  // Tracks the first calendar date that has at least one available time slot
  const [firstAvailableDay, setFirstAvailableDay] = useState(null)

  // Apply appointment-type filter to all available slots
  const filteredAppointments = useMemo(() => {
    if (!selectedAppointmentType) return availableAppointments
    return availableAppointments.filter(
      appointment =>
        appointment?.appointmentType?.id === selectedAppointmentType.id
    )
  }, [availableAppointments, selectedAppointmentType])

  // Determine and store the earliest available slot for the current filter
  // ? May render first available component even if today has appointments
  useEffect(() => {
    if (filteredAppointments.length > 0) {
      const sortedAppointments = filteredAppointments.toSorted(newestToOldest)
      const firstAvailableDay = parse(
        sortedAppointments[0].date,
        'yyyy-MM-dd',
        new Date()
      )
      const availability = getAvailability(
        firstAvailableDay,
        filteredAppointments
      )
      setFirstAvailableDay({
        date: new Date(firstAvailableDay),
        times: availability.times,
      })
    } else {
      // Reset if no appointments found for selected appointment type
      onDaySelected(null)
      setFirstAvailableDay(null)
    }
  }, [filteredAppointments])

  // Update global timezone abbreviation from first available appointment
  // ? May apply incorrect timezone to appointments if a time change occurs in the 60 day query (CDT to CST)
  useEffect(() => {
    if (availableAppointments.length > 0) {
      timezoneAbbr(availableAppointments[0].timezoneAbbr)
    }
  }, [availableAppointments])

  // Handler to jump calendar and selection to today
  const goToToday = () => {
    Analytics.todaySelected({
      locationId,
      partnerId,
    })
    const todayAvailability = getAvailability(today, filteredAppointments)
    if (todayAvailability.times.length > 0) {
      onChangeDate(today)
      onDaySelected(today, todayAvailability)
    } else {
      onChangeDate(today)
      onDaySelected(null)
    }
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <Typography
          className="month-year"
          sx={{
            color: 'var(--intelibly-branding-neutral-900)',
            fontWeight: 500,
          }}
          variant="h6"
        >
          {currentMonthYear}
        </Typography>
        <div className="arrow-button-container">
          <PreviousMonth
            currentDate={currentDate}
            today={today}
            onChangeDate={onChangeDate}
            monthStart={new Date(monthStart)}
          />
          <NextMonth currentDate={currentDate} onChangeDate={onChangeDate} />
        </div>
      </div>
      <Divider variant="secondary" />
      <Button
        aria-label="today"
        color="primary"
        fullWidth
        onClick={() => goToToday()}
        startIcon={<TodayRoundedIcon />}
        variant="text"
      >
        {t('Today')}
      </Button>
      <div className="date-picker">
        <ul className="weekdays">
          {weekdays.map(day => (
            <li key={day}>
              <Typography variant="body2" className="week-day" key={day}>
                {day}
              </Typography>
            </li>
          ))}
        </ul>
        <ol className="days-grid">
          {weeks.map(startOfWeek =>
            eachDayOfInterval({
              start: startOfWeek,
              end: endOfWeek(startOfWeek),
            }).map(day => (
              <li key={formatISO(day, { representation: 'date' })}>
                <Day
                  day={new Date(day)}
                  availability={getAvailability(day, filteredAppointments)}
                  onSelectDay={onDaySelected}
                  selectedDay={selectedDay}
                />
              </li>
            ))
          )}
        </ol>
      </div>
      {!selectedDay && firstAvailableDay && (
        <FirstAvailable
          firstAvailableDay={firstAvailableDay}
          onDaySelected={onDaySelected}
          selectedDay={selectedDay}
          onChangeDate={onChangeDate}
        />
      )}
    </div>
  )
}

Calendar.propTypes = {
  onDaySelected: PropTypes.func.isRequired,
  selectedDay: PropTypes.instanceOf(Date),
  selectedAppointmentType: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  currentDate: PropTypes.instanceOf(Date).isRequired,
  onChangeDate: PropTypes.func.isRequired,
  appointments: PropTypes.array.isRequired,
}
